// Multi-page white-label site: config types + pure helpers. A venue's website
// becomes a set of PAGES, each a stack of typed section BLOCKS, the reusable
// "design a whole site in the builder" system (Love Inn first). Rendering in
// components/site-pages.tsx; editing in the Website builder.

export type SiteBlock =
  | {
      type: "hero";
      imageUrl?: string;
      heading?: string;
      sub?: string;
      ctaLabel?: string;
      ctaHref?: string;
      cta2Label?: string;
      cta2Href?: string;
      /** Viewport-filling (home) vs banner height (inner pages). */
      full?: boolean;
      /** "outline": big bold hollow/stroke-only heading instead of the solid fill. Inner pages only. */
      headingStyle?: "solid" | "outline";
    }
  | { type: "text"; heading?: string; body: string }
  | { type: "imageText"; imageUrl: string; heading?: string; body?: string; flip?: boolean }
  // layout: "masonry" (default) mixes aspect ratios in a Pinterest-style
  // columns layout; "grid" forces every image to a uniform square in an
  // even grid, a neat collage instead.
  | { type: "gallery"; images: string[]; layout?: "masonry" | "grid" }
  | { type: "events"; heading?: string; limit?: number }
  // eventsCarousel: horizontal snap carousel of square posters. Two modes:
  //   1. Pull live Assembly events (venue with hasApp + tickets via us) —
  //      leave `items` unset.
  //   2. Static list, same shape as `agenda.items` — for venues whose tickets
  //      live off-platform (Love Inn on HeadFirst) but who still want the
  //      carousel look.
  | { type: "eventsCarousel"; heading?: string; limit?: number; items?: Array<{ date: string; title: string; sub?: string; imageUrl?: string; href: string }> }
  | {
      type: "agenda";
      heading?: string;
      /** Cap how many rows render (e.g. a trimmed "Coming up" on the home page). */
      limit?: number;
      items: Array<{ date: string; title: string; sub?: string; imageUrl?: string; href: string }>;
    }
  | { type: "faq"; heading?: string; items: Array<{ q: string; a: string }>; imageUrl?: string; flip?: boolean }
  | { type: "hours"; heading?: string; rows: Array<{ label: string; value: string }>; note?: string; imageUrl?: string; flip?: boolean }
  | { type: "contact"; heading?: string; body?: string; email?: string; phone?: string; address?: string; imageUrl?: string; flip?: boolean }
  | { type: "audio"; heading?: string; sub?: string; items: Array<{ title: string; href: string; imageUrl?: string; meta?: string }> }
  // Big clickable location/venue tiles (the "pick a site" splash, e.g. Next
  // Door's Shepherd's Bush / Stoke Newington / Online Shop landing).
  | { type: "tiles"; heading?: string; items: Array<{ title: string; sub?: string; href: string; imageUrl?: string }> }
  // Full-viewport monochrome teaser / coming-soon splash (NDR's Red Room):
  // a kicker line, one huge wordmark, an optional sub-line + tagline, and an
  // optional mailing-list signup. Best paired with page.bare = true.
  | {
      type: "splash";
      backgroundColor?: string;
      foregroundColor?: string;
      kicker?: string;
      heading: string;
      sub?: string;
      tagline?: string;
      showMailingList?: boolean;
      mailingListPlaceholder?: string;
      /** Which named list on this venue to add signups to (default: the primary list). */
      listId?: string;
      // Font overrides. Either a Google Fonts family name (loaded via a link
      // tag on the fly), or a paired {name, url} to inject an @font-face for a
      // hosted custom font file (paid foundry fonts like BHH Bartle). URL wins
      // when both are set.
      headingFont?: string;
      bodyFont?: string;
      headingFontUrl?: string;
      bodyFontUrl?: string;
    }
  // imageUrl (optional): renders through the same image+content split used
  // by imageText/hours/faq, on the venue's normal dark background, instead
  // of today's centered white inverted panel. flip puts the image on the
  // right. Absent = unchanged white-panel behaviour.
  // listId (optional): named list on this venue to add signups to.
  // collectPhone / collectSmsConsent: opt in to capturing phone + PECR SMS
  // consent alongside email, so the SMS platform actually has audience.
  // Both default false → email-only form (existing behaviour).
  | {
      type: "subscribe";
      heading?: string;
      sub?: string;
      imageUrl?: string;
      flip?: boolean;
      listId?: string;
      collectPhone?: boolean;
      collectSmsConsent?: boolean;
    }
  | { type: "cta"; label: string; href: string }
  | {
      type: "appPromo";
      heading?: string;
      body?: string;
      /** Up to 3 app screenshots, shown as a fanned iPhone-mockup cluster. */
      screenshots: string[];
      ctaLabel?: string;
      ctaHref?: string;
    };

export type SitePage = {
  /** URL path segment; "" = the home page. */
  slug: string;
  title: string;
  /** Shown in the burger/nav menu. */
  inNav?: boolean;
  /** Shown as an always-visible primary button in the header. */
  primary?: boolean;
  /** Listed in the footer (privacy/terms). */
  footer?: boolean;
  /** Suppress the site nav + footer chrome (single-purpose splash pages). */
  bare?: boolean;
  /**
   * Draft vs live. Absent or true = live on the public site. False = draft:
   * hidden from nav/footer/direct-URL access for real visitors, still fully
   * rendered inside the admin preview iframe so operators can build a page
   * without it going live on save. Back-compat: pre-existing pages have no
   * `published` field, so they read as live (undefined !== false).
   */
  published?: boolean;
  blocks: SiteBlock[];
};

export type SitePagesConfig = {
  pages: SitePage[];
};

/**
 * Is this page visible to public visitors right now? Draft pages are hidden
 * from nav/footer/direct-URL access. Absent `published` = live for back-compat
 * with every page that existed before the draft flag was introduced.
 */
export function isPageLive(page: SitePage): boolean {
  return page.published !== false;
}

/**
 * The page for a pathname ("/" -> slug "", "/whats-on" -> "whats-on"). By
 * default filters draft pages out (they behave as if they don't exist for
 * public visitors); pass `{ includeDrafts: true }` from the admin preview
 * iframe so operators can navigate to a page they haven't published yet.
 */
export function pageForPath(
  cfg: SitePagesConfig | undefined | null,
  pathname: string,
  opts?: { includeDrafts?: boolean },
): SitePage | null {
  if (!cfg?.pages?.length) return null;
  const slug = pathname.replace(/^\/+|\/+$/g, "");
  const match = cfg.pages.find((p) => (p.slug ?? "") === slug) ?? null;
  if (!match) return null;
  if (opts?.includeDrafts) return match;
  return isPageLive(match) ? match : null;
}

export const BLOCK_LABELS: Record<SiteBlock["type"], string> = {
  hero: "Hero (image + heading + buttons)",
  text: "Text",
  imageText: "Image + text",
  gallery: "Photo gallery",
  events: "What's on (live events)",
  eventsCarousel: "Upcoming events (horizontal carousel, from the app)",
  agenda: "Agenda (grouped by month, static list)",
  faq: "FAQ / accordion",
  hours: "Opening hours",
  contact: "Contact details",
  audio: "Audio archive (links)",
  tiles: "Location tiles (big clickable cards)",
  splash: "Splash / coming soon (full-viewport teaser)",
  subscribe: "Mailing-list signup",
  cta: "Button",
  appPromo: "Get the app (phone mockups + store link)",
};

/** A sensible empty block of each type (the builder's "Add block"). */
export function emptyBlock(type: SiteBlock["type"]): SiteBlock {
  switch (type) {
    case "hero":
      return { type, heading: "", sub: "" };
    case "text":
      return { type, heading: "", body: "" };
    case "imageText":
      return { type, imageUrl: "", heading: "", body: "" };
    case "gallery":
      return { type, images: [] };
    case "events":
      return { type, heading: "What's on", limit: 12 };
    case "eventsCarousel":
      return { type, heading: "Upcoming events", limit: 12 };
    case "agenda":
      return { type, heading: "Coming up", items: [] };
    case "faq":
      return { type, items: [] };
    case "hours":
      return { type, heading: "Opening hours", rows: [] };
    case "contact":
      return { type, heading: "Contact" };
    case "audio":
      return { type, heading: "Audio archive", items: [] };
    case "tiles":
      return { type, items: [] };
    case "splash":
      return {
        type,
        backgroundColor: "#B80011",
        foregroundColor: "#422126",
        heading: "Coming soon",
        tagline: "coming soon...",
        showMailingList: true,
      };
    case "subscribe":
      return { type, heading: "Subscribe", sub: "" };
    case "cta":
      return { type, label: "", href: "" };
    case "appPromo":
      return { type, heading: "Get the app", screenshots: [], ctaLabel: "Download now" };
  }
}
